<template>
  <main class="newDesignSignInUpMain">
    <!-- MFA Verification Screen -->
    <two-factor-auth
      v-if="showMfa"
      :prospectId="mfaProspectId"
      @otp-verified="onOtpVerified"
    />

    <!-- Normal Sign In / Sign Up -->
    <div v-else class="wrapper h-auto">
      <div class="sign-in-up newDesignSignInUp">
        <div class="overlay" id="sign-in"></div>
        <div class="form">
          <div class="form__content">
            <div class="newDesignSignInUpHead">
              <div class="logo">
                <img v-if="teamLogo" :src="teamLogo" alt="Team-Logo" />
              </div>
              <h2>{{ pageTitle }}</h2>
            </div>

            <div class="newSignInUpTabWrapper">
              <div class="tab-wrapper">
                <ul class="tabs">
                  <li
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="tab-link"
                    :class="{ active: activeTab === tab.id }"
                    @click="setTab(tab.id)"
                  >
                    {{ tab.label }}
                  </li>
                </ul>
              </div>

              <div class="content-wrapper">
                <!-- Common Social Login -->
                <div class="social-icons">
                  <ul>
                    <li>
                      <button
                        @click="googleSignIn"
                        type="button"
                        class="active"
                      >
                        <img
                          src="../assets/images/google-ico.svg"
                          alt="google icon"
                        />
                        <span>{{
                          translatedData?.googleSignInButtonText
                        }}</span>
                      </button>
                    </li>
                    <li>
                      <v-facebook-login
                        class="facebook-login"
                        app-id="497045238736125"
                        version="v25.0"
                        v-model="fbModel"
                        @sdk-init="facebookSdkInit"
                      ></v-facebook-login>
                    </li>
                    <li>
                      <button type="button" @click="linkedInPopup">
                        <img
                          src="../assets/images/linkedin-ico.svg"
                          alt="linkedin icon"
                        />
                      </button>
                    </li>
                  </ul>
                </div>
                <div class="divider">
                  <span>{{ translatedData?.orText }}</span>
                </div>

                <!-- Sign In Tab -->
                <div v-show="isSignIn" class="tab-item active">
                  <form @submit.prevent="appLogin" @keyup.enter="appLogin">
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.emailLabel
                      }}</label>
                      <input
                        v-model.trim="$v.email.$model"
                        type="email"
                        inputmode="email"
                        :class="{ 'is-invalid': $v.email.$error }"
                        class="form-control"
                      />
                      <small class="invalid-feedback" v-if="!$v.email.required">
                        {{ translatedData?.emailRequiredError }}
                      </small>
                      <small
                        class="invalid-feedback"
                        v-else-if="!$v.email.email"
                      >
                        {{ translatedData?.emailInvalidError }}
                      </small>
                    </div>
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.passwordLabel
                      }}</label>
                      <div class="input-group">
                        <input
                          v-model.trim="$v.password.$model"
                          :type="showPass ? 'text' : 'password'"
                          class="form-control"
                          :class="{ 'is-invalid': $v.password.$error }"
                        />
                        <div class="input-group-append">
                          <span
                            @click="showPass = !showPass"
                            class="input-group-text input-group-eye bg-transparent"
                          >
                            <i
                              :class="
                                showPass ? 'far fa-eye' : 'fas fa-eye-slash'
                              "
                            ></i>
                          </span>
                        </div>
                      </div>
                      <small
                        class="invalid-feedback"
                        :class="{
                          'd-block':
                            $v.password.$error && !$v.password.required,
                        }"
                      >
                        {{ translatedData?.passwordRequiredError }}
                      </small>
                      <div
                        class="d-flex align-items-center justify-content-end mt-1 forgotpassCover"
                      >
                        <router-link
                          to="/forgot-password"
                          class="forgot-password"
                        >
                          <span>{{
                            translatedData?.forgotPasswordLinkText
                          }}</span>
                        </router-link>
                      </div>
                    </div>
                    <div class="backBtnContinue">
                      <div class="button w-100">
                        <button
                          type="button"
                          class="btn btn-cancel w-100"
                          @click="goBack"
                        >
                          <span>{{ translatedData?.backButtonText }}</span>
                        </button>
                      </div>
                      <div class="button w-100">
                        <button
                          type="button"
                          class="btn w-100"
                          :class="{ 'btn-red': isSignInValid }"
                          :disabled="!isSignInValid"
                          @click="appLogin"
                        >
                          <span>{{ translatedData?.continueButtonText }}</span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

                <!-- Sign Up Tab -->
                <div v-show="isSignUp" class="tab-item active">
                  <form @submit.prevent="signUp" @keyup.enter="signUp">
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.firstNameLabel
                      }}</label>
                      <input
                        v-model.trim="$v.firstName.$model"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': $v.firstName.$error }"
                      />
                      <small
                        class="invalid-feedback"
                        v-if="!$v.firstName.required"
                      >
                        {{ translatedData?.firstNameRequiredError }}
                      </small>
                    </div>
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.lastNameLabel
                      }}</label>
                      <input
                        v-model.trim="$v.lastName.$model"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': $v.lastName.$error }"
                      />
                      <small
                        class="invalid-feedback"
                        v-if="!$v.lastName.required"
                      >
                        {{ translatedData?.lastNameRequiredError }}
                      </small>
                    </div>
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.emailLabel
                      }}</label>
                      <input
                        v-model.trim="$v.signUpEmail.$model"
                        type="email"
                        inputmode="email"
                        class="form-control"
                        :class="{ 'is-invalid': $v.signUpEmail.$error }"
                      />
                      <small
                        class="invalid-feedback"
                        v-if="!$v.signUpEmail.required"
                      >
                        {{ translatedData?.emailRequiredError }}
                      </small>
                      <small
                        class="invalid-feedback"
                        v-else-if="!$v.signUpEmail.email"
                      >
                        {{ translatedData?.emailInvalidError }}
                      </small>
                    </div>
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.passwordLabel
                      }}</label>
                      <div class="input-group">
                        <input
                          v-model.trim="$v.signUpPassword.$model"
                          :type="showSignUpPass ? 'text' : 'password'"
                          class="form-control"
                          :class="{ 'is-invalid': $v.signUpPassword.$error }"
                        />
                        <div class="input-group-append">
                          <span
                            @click="showSignUpPass = !showSignUpPass"
                            class="input-group-text input-group-eye bg-transparent"
                          >
                            <i
                              :class="
                                showSignUpPass
                                  ? 'far fa-eye'
                                  : 'fas fa-eye-slash'
                              "
                            ></i>
                          </span>
                        </div>
                      </div>
                      <div v-if="$v.signUpPassword.$error">
                        <small
                          v-if="!$v.signUpPassword.required"
                          class="invalid-feedback d-block"
                        >
                          {{ translatedData?.passwordRequiredError }}
                        </small>
                        <small
                          v-else-if="!$v.signUpPassword.maxLength"
                          class="d-block invalid-feedback"
                        >
                          {{ translatedData?.passwordMaxLengthError1 }}
                          {{ $v.signUpPassword.$params.maxLength.max }}
                          {{ translatedData?.passwordMaxLengthError2 }}
                        </small>
                      </div>
                    </div>
                    <div class="form-group">
                      <label class="form-label">{{
                        translatedData?.confirmPasswordLabel
                      }}</label>
                      <div class="input-group">
                        <input
                          v-model.trim="$v.confirmPassword.$model"
                          :type="showConfirmPass ? 'text' : 'password'"
                          class="form-control"
                          :class="{ 'is-invalid': $v.confirmPassword.$error }"
                        />
                        <div class="input-group-append">
                          <span
                            @click="showConfirmPass = !showConfirmPass"
                            class="input-group-text input-group-eye bg-transparent"
                          >
                            <i
                              :class="
                                showConfirmPass
                                  ? 'far fa-eye'
                                  : 'fas fa-eye-slash'
                              "
                            ></i>
                          </span>
                        </div>
                      </div>
                      <div v-if="$v.confirmPassword.$error">
                        <small
                          v-if="!$v.confirmPassword.required"
                          class="d-block invalid-feedback"
                        >
                          {{ translatedData?.confirmPasswordRequiredError }}
                        </small>
                        <small
                          v-else-if="!$v.confirmPassword.sameAs"
                          class="d-block invalid-feedback"
                        >
                          {{ translatedData?.confirmPasswordMismatchError }}
                        </small>
                      </div>
                    </div>
                    <div class="backBtnContinue">
                      <div class="button w-100">
                        <button
                          type="button"
                          class="btn btn-cancel w-100"
                          @click="goBack"
                        >
                          <span>{{ translatedData?.backButtonText }}</span>
                        </button>
                      </div>
                      <div class="button w-100">
                        <button
                          type="button"
                          class="btn w-100"
                          :class="{ 'btn-red': isSignUpValid }"
                          :disabled="!isSignUpValid"
                          @click="signUp"
                        >
                          <span>{{ translatedData?.continueButtonText }}</span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { required, email, maxLength, sameAs } from "vuelidate/lib/validators";
import VFacebookLogin from "vue-facebook-login-component";
import commonFunctions from "./../mixins/commonfunctions";
import { translationMixin } from "./../mixins/translationMixin";
import cloneDeep from "lodash/cloneDeep";
import TwoFactorAuth from "./TwoFactorAuth.vue";

const TAB_SIGN_IN = 1;
const TAB_SIGN_UP = 2;

const defaultData = {
  signInPageTitle: "Sign in to pre-fill your mortgage application",
  signUpPageTitle: "Sign up to complete your mortgage application",
  googleSignInButtonText: "Continue with Google",
  orText: "Or",
  emailLabel: "Email",
  emailRequiredError: "Email field is required.",
  emailInvalidError: "Please enter a valid email address.",
  passwordLabel: "Password",
  passwordRequiredError: "Password field is required.",
  forgotPasswordLinkText: "Forgot Password?",
  continueButtonText: "Continue",
  backButtonText: "Back",
  firstNameLabel: "First Name",
  lastNameLabel: "Last Name",
  confirmPasswordLabel: "Confirm Password",
  firstNameRequiredError: "First Name field is required.",
  lastNameRequiredError: "Last Name field is required.",
  passwordMaxLengthError1: "Maximum",
  passwordMaxLengthError2: "characters are allowed.",
  confirmPasswordRequiredError: "Confirm Password field is required.",
  confirmPasswordMismatchError:
    "Password and Confirm Password fields do not match.",
};

export default {
  name: "SignInUp",
  mixins: [commonFunctions, translationMixin],
  components: {
    VFacebookLogin,
    TwoFactorAuth,
  },
  data() {
    return {
      // Sign In fields
      email: "",
      password: "",
      showPass: false,
      // Sign Up fields
      firstName: "",
      lastName: "",
      signUpEmail: "",
      signUpPassword: "",
      confirmPassword: "",
      showSignUpPass: false,
      showConfirmPass: false,
      // Shared
      signInBg: "",
      teamLogo: "",
      fbData: {},
      fbScope: {},
      fbModel: { connected: false },
      data: defaultData,
      // MFA
      showMfa: false,
      mfaProspectId: "",
      pendingAuthResp: null,
      pendingAuthEmail: "",
      // Tabs
      activeTab: TAB_SIGN_IN,
      tabs: [
        { id: TAB_SIGN_IN, label: "Sign in" },
        { id: TAB_SIGN_UP, label: "Sign Up" },
      ],
    };
  },
  validations: {
    // Sign In
    email: { required, email },
    password: { required },
    // Sign Up
    firstName: { required },
    lastName: { required },
    signUpEmail: { required, email },
    signUpPassword: { required, maxLength: maxLength(25) },
    confirmPassword: { required, sameAs: sameAs("signUpPassword") },
  },
  computed: {
    ...mapGetters([
      "getOrgId",
      "getAppFlow",
      "getUserId",
      "getProspectId",
      "getSignupUserId", // Steven change: DMA co-brand
      "getIsAuthenticated",
      "getAssets",
      "getQuestions",
      "getActiveQuestion",
      "getCoPilotSession",
      "getHomePageTailUrl",
      "getEditModeFlag",
      "getActivePage",
    ]),
    baseUrl() {
      return "/" + this.getHomePageTailUrl;
    },
    isSignIn() {
      return this.activeTab === TAB_SIGN_IN;
    },
    isSignUp() {
      return this.activeTab === TAB_SIGN_UP;
    },
    pageTitle() {
      return this.isSignIn
        ? this.translatedData?.signInPageTitle
        : this.translatedData?.signUpPageTitle;
    },
    isSignInValid() {
      return !this.$v.email.$invalid && !this.$v.password.$invalid;
    },
    isSignUpValid() {
      return (
        !this.$v.firstName.$invalid &&
        !this.$v.lastName.$invalid &&
        !this.$v.signUpEmail.$invalid &&
        !this.$v.signUpPassword.$invalid &&
        !this.$v.confirmPassword.$invalid
      );
    },
  },
  watch: {
    $route: {
      immediate: true,
      deep: true,
      async handler(newVal) {
        if (newVal.query.code) await this.linkedInSignIn();
        if (newVal.query.error) this.$router.push("/sign-in");
      },
    },
    fbModel(newVal, oldVal) {
      if (newVal.connected === true && oldVal.connected === undefined) {
        this.fbData.getLoginStatus(async (response) => {
          if (response.status === "connected") {
            await this.fbData.logout(() => {});
            await this.fbScope.logout();
          }
        });
      }
      this.fetchFacebookModel(newVal);
      if (newVal.connected === true && oldVal.connected === false) {
        this.facebookSignin();
      }
    },
    signInBg(newVal) {
      if (newVal) {
        const theBox = document.getElementById("sign-in");
        theBox.style.backgroundImage = `url(${newVal})`;
        theBox.style.backgroundSize = "cover";
      }
    },
  },
  beforeMount() {
    if (this.getIsAuthenticated) this.$router.push("/switch-application");
    this.signInBg = this.getAssets.agentPortalBackground;
    this.teamLogo = this.getAssets.horizontalTeamLogo;
  },
  mounted() {
    if (this.getAppFlow === "" && !Object.keys(this.$route.query).length) {
      this.fetchAppFlow("Buying");
    }
    if (!this.getQuestions.length && !Object.keys(this.$route.query).length) {
      this.getQuestionnaire();
    }
    this.fetchLoaderStatus(false);
  },
  methods: {
    ...mapActions([
      "fetchProspectEmail",
      "fetchAppFlow",
      "fetchApplications",
      "fetchIsAuthenticated",
      "fetchShowInternalAuth",
      "fetchActiveQuestion",
      "fetchActiveSection",
      "fetchSections",
      "fetchQuestions",
      "fetchSubQuestions",
      "fetchLoaderStatus",
      "fetchSocialLogin",
      "fetchFacebookData",
      "fetchFacebookScope",
      "fetchFacebookModel",
      "fetchUserId",
      "fetchUser",
      "updateQueObjectWithAnswer",
      "fetchActivePage",
      "fetchEditModeFlag",
    ]),

    setTab(tabId) {
      this.activeTab = tabId;
    },

    goBack() {
      this.$router.push(
        this.getQuestions.length ? "/questionnaire" : this.baseUrl
      );
    },

    resetForms() {
      // Sign In
      this.email = "";
      this.password = "";
      // Sign Up
      this.firstName = "";
      this.lastName = "";
      this.signUpEmail = "";
      this.signUpPassword = "";
      this.confirmPassword = "";
      this.$v.$reset();
    },

    async continueWithApplication(app) {
      this.fetchActivePage(true);
      this.fetchActiveQuestion(1);
      this.fetchActiveSection("Getting Started");
      this.fetchLoaderStatus(true);

      if (app.type !== this.getAppFlow) await this.getQuestionnaire(app);

      const theQues = cloneDeep(this.getQuestions);

      try {
        const response = await this.$http.get(
          "/account/getSingleQuestionAnswer?prospectId=" +
            app.prospectId +
            "&orgId=" +
            this.getOrgId +
            "&flowName=" +
            app.type
        );
        const resp = response.data;

        if (resp.response === "Success") {
          this.fetchUserId(resp.prospectId);
          this.fetchUser(resp.prospectdetails);
          this.updateQueObjectWithAnswer(resp.appQuestions);
          this.flushPendingAppLanguage(resp.prospectId);
          if (theQues.length && this.getUserId) {
            await this.storeUnauthenticatedQuestions(theQues);
          }
          if (this.getEditModeFlag) this.fetchEditModeFlag(false);
          this.$router.push("/questionnaire");
        } else {
          this.err(resp.message);
          this.fetchLoaderStatus(false);
        }
      } catch (error) {
        console.log(error);
        this.fetchLoaderStatus(false);
      }
    },

    async storeUnauthenticatedQuestions(data) {
      const unsavedQues = data.filter(
        (v) =>
          !v.needsAuthentication &&
          v.apiName === "LockLearProspectAuth" &&
          v.questionAnswer != null &&
          v.questionAnswer !== ""
      );

      if (!unsavedQues.length || !this.getUserId) return;

      try {
        const response = await this.$http.post(
          "/question/prospect/Questions/",
          {
            prospectId: this.getUserId,
            questionsToUpdate: unsavedQues,
            orgId: this.getOrgId,
          }
        );
        const resp = response.data;

        if (resp.response === "Success") {
          this.updateQueObjectWithAnswer(resp.appQuestions);
        } else {
          this.err(resp.message);
        }
      } catch (error) {
        this.err(error);
        console.log(error);
      }
    },

    async getQuestionnaire(app) {
      const user_id = app ? app.prospectId : this.getUserId || null;
      if (app) this.fetchAppFlow(app.type);

      try {
        const response = await this.$http.get(
          "/question/GetProspectQuestions/" +
            this.getAppFlow +
            "/" +
            user_id +
            "?orgId=" +
            this.getOrgId
        );
        const resp = response.data;

        if (resp.response === "Success") {
          this.fetchSections(resp.sidebarSections);
          this.fetchQuestions(resp.appQuestions);
          this.fetchSubQuestions(resp.subQuestions);
          this.fetchActiveQuestion(1);
          if (this.getQuestions.length) {
            const getSectionName = this.getQuestions.find(
              (v) => v.questionIndex === this.getActiveQuestion
            );
            this.fetchActiveSection(getSectionName.sectionName);
          }
        } else {
          this.err(resp.message);
        }
      } catch (error) {
        this.err(error);
        console.log(error);
      }
    },

    pickProspectIdFromApps(applications) {
      if (!applications || !applications.length) return null;

      const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split("/");
        const month = parseInt(parts[0], 10) || 1;
        const day = parseInt(parts[1], 10) || 1;
        const year = parseInt(parts[2], 10) || 1970;
        return new Date(year, month - 1, day).getTime();
      };

      // First priority: latest incomplete application (no endDate)
      const incomplete = applications.filter((app) => !app.endDate);
      if (incomplete.length) {
        const sorted = [...incomplete].sort(
          (a, b) => parseDate(b.startDate) - parseDate(a.startDate)
        );
        return sorted[0].prospectId;
      }

      // Second priority: latest complete application
      const complete = applications.filter((app) => app.endDate);
      if (complete.length) {
        const sorted = [...complete].sort(
          (a, b) => parseDate(b.endDate) - parseDate(a.endDate)
        );
        return sorted[0].prospectId;
      }

      return null;
    },

    async handleAuthSuccess(resp, prospectEmail, skipMfa = false) {
      if (!skipMfa) {
        // Store auth data and show MFA screen.
        // Keep the loader on — TwoFactorAuth will manage it through
        // GetAsset + auto-send OTP, then turn it off.
        this.pendingAuthResp = resp;
        this.pendingAuthEmail = prospectEmail;
        this.mfaProspectId =
          this.pickProspectIdFromApps(resp.applications) ||
          resp.prospectId ||
          this.getProspectId;
        this.showMfa = true;
        return;
      }

      // MFA verified — proceed with original flow
      this.fetchIsAuthenticated(true);
      this.fetchProspectEmail(prospectEmail);
      this.fetchApplications(resp.applications);
      this.resetForms();

      const pendingApps = resp.applications.filter((app) => !app.endDate);

      if (pendingApps.length === 1) {
        this.fetchShowInternalAuth(false);
        await this.continueWithApplication(pendingApps[0]);
      } else {
        this.fetchLoaderStatus(false);
        this.$router.push("/switch-application");
        this.fetchShowInternalAuth(false);
      }
    },

    async onOtpVerified() {
      // OTP verified — resume auth with stored data
      this.success("Login Successful");
      await this.handleAuthSuccess(
        this.pendingAuthResp,
        this.pendingAuthEmail,
        true
      );
    },

    async appLogin(emailOverride, passwordOverride) {
      this.fetchLoaderStatus(true);

      const prospectEmail =
        typeof emailOverride === "string" ? emailOverride : this.email;
      const prospectPassword =
        typeof passwordOverride === "string" ? passwordOverride : this.password;

      try {
        const response = await this.$http.post("/account/Prospect/Auth", {
          authenticated: false,
          flowName: this.getAppFlow,
          prospectEmail,
          prospectPassword,
          prospectFirstName: "",
          prospectLastName: "",
          orgId: this.getOrgId,
          userId: this.getSignupUserId, // Steven change: DMA co-brand - realtor Account id when co-brand is active, else the LO/record id as before
          isCopilotSession: this.getCoPilotSession,
        });
        const resp = response.data;

        if (resp.response === "Success") {
          await this.handleAuthSuccess(resp, prospectEmail);
          // When MFA is shown, TwoFactorAuth owns the loader from here on.
          if (this.showMfa) return;
          // No MFA path (shouldn't happen for password login, but stay safe)
          this.success("Login Successful");
        } else {
          this.err(resp.message);
        }
      } catch (error) {
        this.err(error);
        console.log(error);
      }

      this.fetchLoaderStatus(false);
    },

    async signUp() {
      this.fetchLoaderStatus(true);

      try {
        const response = await this.$http.post("/account/prospect/register", {
          prospectFirstName: this.capitalizeFirstChar(this.firstName),
          prospectLastName: this.capitalizeFirstChar(this.lastName),
          prospectEmail: this.signUpEmail,
          password: this.signUpPassword,
          orgId: this.getOrgId,
          userId: this.getSignupUserId, // Steven change: DMA co-brand - realtor Account id when co-brand is active, else the LO/record id as before
          flowName: this.getAppFlow,
        });
        const resp = response.data;

        if (resp.response === "Success") {
          this.success(
            "Registration successful. Please wait while we sign you in automatically."
          );
          const emailTemp = this.signUpEmail;
          const passTemp = this.signUpPassword;
          await this.appLogin(emailTemp, passTemp);
        } else {
          this.err(resp.message);
          this.fetchLoaderStatus(false);
        }
      } catch (error) {
        this.err(error);
        console.log(error);
        this.fetchLoaderStatus(false);
      }
    },

    async socialLogin(payload, platform) {
      if (platform === "LinkedIn") this.fetchLoaderStatus(true);

      try {
        const response = await this.$http.post(
          "/account/Prospect/Auth",
          payload
        );
        const resp = response.data;

        if (resp.response === "Success") {
          this.success("Login Successful");
          this.fetchSocialLogin(platform);
          await this.handleAuthSuccess(resp, payload.prospectEmail, true);
        } else {
          this.err(resp.message);
        }
      } catch (error) {
        this.err(error);
        console.log(error);
      }

      this.fetchLoaderStatus(false);
    },

    buildSocialPayload(email, firstName, lastName) {
      return {
        authenticated: true,
        flowName: this.getAppFlow,
        prospectEmail: email,
        prospectFirstName: this.capitalizeFirstChar(firstName || ""),
        prospectLastName: this.capitalizeFirstChar(lastName || ""),
        orgId: this.getOrgId,
        userId: this.getSignupUserId, // Steven change: DMA co-brand - realtor Account id when co-brand is active, else the LO/record id as before
        prospectPassword: this.generatePass(17),
      };
    },

    async googleSignIn() {
      try {
        this.fetchLoaderStatus(true);
        const googleUser = await this.$googleAuth.signIn();

        if (!googleUser) {
          throw new Error("Google sign-in was cancelled");
        }

        const nameParts = (googleUser.name || "").split(" ");
        const payload = this.buildSocialPayload(
          googleUser.email,
          googleUser.given_name || nameParts[0],
          googleUser.family_name || nameParts[1]
        );

        await this.socialLogin(payload, "Google");
      } catch (error) {
        console.error("Google Sign-In Error:", error);
        this.err(
          error.message === "User cancelled"
            ? "Sign-in was cancelled"
            : error.message || "Google sign-in failed"
        );
        this.fetchLoaderStatus(false);
      }
    },

    async linkedInSignIn() {
      this.fetchLoaderStatus(true);
      const code = this.$route.query.code;

      if (!code) return;

      try {
        const response = await this.$http.get(
          "https://digitalmortgageapi.candidcrm.com/socialLogin/linkedin/getAccessTokenWithUserData?code=" +
            code
        );
        const payload = this.buildSocialPayload(
          response.data.emailAddress,
          response.data.localizedFirstName,
          response.data.localizedLastName
        );

        await this.socialLogin(payload, "LinkedIn");
      } catch (error) {
        console.log(error);
        this.fetchLoaderStatus(false);
      }
    },

    linkedInPopup() {
      window.location =
        "https://www.linkedin.com/oauth/v2/authorization?response_type=code&state=987654321&client_id=77urg3qa0jbwtg&redirect_uri=https://digitalmortgage.candidcrm.com/sign-in/&scope=r_liteprofile,r_emailaddress";
    },

    facebookSdkInit({ FB, scope }) {
      this.fetchFacebookData(FB);
      this.fetchFacebookScope(scope);
      this.fbData = FB;
      this.fbScope = scope;
    },

    facebookSignin() {
      this.fetchLoaderStatus(true);

      this.fbData.api("/me", "GET", { fields: "name,email" }, (user) => {
        const nameParts = (user.name || "").split(" ");
        const payload = {
          ...this.buildSocialPayload(user.email, nameParts[0], nameParts[1]),
          authenticated: this.fbModel.connected,
        };

        this.socialLogin(payload, "Facebook");
      });
    },

    generatePass(n) {
      const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$";
      let password = "";
      for (let i = 0; i < n; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    },
  },
};
</script>

<style scoped>
.forgot-password {
  font-weight: 600;
  font-size: 13px;
  color: var(--primary-color);
  width: 100%;
  text-align: right;
}

.newDesignSignInUp {
  display: inline-flex;
}

.sign-in-up .overlay {
  opacity: 1;
  position: fixed;
}

.newDesignSignInUpMain {
  display: inline-flex;
  width: 100%;
  align-items: center;
  height: auto;
}

.newDesignSignInUp .form__content {
  background-color: #f8f5f5 !important;
  padding: 0px !important;
  border-radius: 0px 0px 20px 20px !important;
}
.newDesignSignInUpHead {
  padding: 44px;
  text-align: center;
  width: 100%;
}
.newSignInUpTabWrapper {
  width: 100%;
}
.newSignInUpTabWrapper .tab-wrapper .tabs {
  display: inline-flex;
  width: 100%;
  align-items: center;
  flex-wrap: nowrap;
  flex-direction: row;
}
.newSignInUpTabWrapper .tab-wrapper .tabs .tab-link {
  width: 50%;
  text-align: center;
  padding: 20px 15px;
  color: #787878;
  font-size: 18px;
  cursor: pointer;
}
.newSignInUpTabWrapper .tab-wrapper .tabs .tab-link.active {
  background-color: #ffffff;
  border-radius: 8px 8px 0px 0px;
  color: #000000;
}

.newSignInUpTabWrapper .content-wrapper {
  background-color: #ffffff;
  width: 100%;
  padding: 35px;
  border-radius: 0px 0px 20px 20px !important;
}
.newSignInUpTabWrapper .content-wrapper .tab-item {
  display: none;
  width: 100%;
}
.newSignInUpTabWrapper .content-wrapper .tab-item.active {
  display: inline-block;
}
.backBtnContinue {
  display: inline-flex;
  width: 100%;
  align-items: center;
  flex-wrap: nowrap;
  gap: 20px;
}

.sign-in-up .form {
  height: 100vh !important;
  margin-top: 30px !important;
  margin: 0px;
}

.newSignInUpTabWrapper .social-icons ul {
  justify-content: center;
}
.newSignInUpTabWrapper .social-icons ul li button {
  border-radius: 10px;
}
.forgotpassCover a {
  font-size: 16px !important;
  margin-top: 12px;
}

@media (max-height: 1200px) {
  .sign-in-up .form {
    padding-top: 50px;
    padding-bottom: 50px;
    height: auto !important;
  }
}
</style>

<style>
.sign-in-up .form .form__content .social-icons ul li .facebook-login {
  cursor: pointer;
  max-width: 45px;
  min-width: unset;
  font-size: 0;
  padding: 0;
  position: relative;
  background: transparent url(../assets/images/facebook-ico.svg) no-repeat;
  background-position: center;
}

.sign-in-up .form .form__content .social-icons ul li .facebook-login svg {
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
  color: #4267b2;
  position: absolute;
  display: none;
}

.sign-in-up .form .form__content .social-icons ul li .facebook-login .loader {
  width: 20px;
  opacity: 1;
  visibility: visible;
  padding: 10px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  display: none;
}
</style>